/**
 * @prettier
 */

import { isMobile, isTablet } from "react-device-detect";
import apiService from "../../services/api/api";
import DataDogService from "../../services/shared/datadogService";
import logService from "../../services/shared/logService";
import { runSubmissionCompletionSideEffects } from "../../services/shared/submissionCompletion";
import { history, store } from "../store";

const IS_OCR_VERIFICATION_ENABLED =
    process.env.REACT_APP_SHOW_OCR_VERIFICATION === "true";

// Utility function to check if OCR verification is needed
const isOcrVerificationNeeded = state => {
    return (
        IS_OCR_VERIFICATION_ENABLED &&
        (state.screens.ocr_verification.enabled ||
            (!state.screens.e_verification.visible &&
                (state.screens.ocr_verification.enabled ||
                    state.screens.e_verification.enabled))) &&
        !state.submissionState.ocr_verification.completed
    );
};

// Navigate to the next page based on page and state
export function navigateNext(page, state, props, params) {
    let next_page = "/";
    let finalize = false;

    switch (page) {
        case "app_entry":
            next_page = nextFromAppEntry(state);
            finalize = true;
            break;

        case "start_screen":
            next_page = firstUncompletedStage(state);
            finalize = true;
            break;

        case "group_link_start_page":
            next_page = nextFromGroupLinkStart();
            break;

        case "re_verification_start":
            next_page = nextFromReverificationStart(state, props);
            finalize = true;
            break;

        case "e_verification":
            next_page = nextFromEVerification(state, props);
            break;

        case "document_selection":
            next_page = nextFromDocumentSelection(state, props);
            break;

        case "instructions":
            next_page = nextFromInstructions(props);
            break;

        case "document_capture":
            next_page = nextFromDocumentCapture(state, props);
            break;

        case "document_review":
            next_page = nextFromDocumentReview(state, props);
            finalize = true;
            break;

        case "capture_photo":
            next_page = nextFromCapturePhoto(props);
            finalize = true;
            break;

        case "ocr_verification":
            next_page = nextFromOcrVerification(state, props);
            finalize = true;
            break;

        case "consent":
            next_page = nextFromConsent();
            break;

        case "proof_of_address":
            next_page = nextFromProofOfAddress(state, props);
            finalize = true;
            break;

        case "passive_liveness":
            next_page = nextFromPassiveLiveness(props);
            finalize = true;
            break;
        default:
            return;
    }

    if (next_page === "/success" && finalize === true) {
        finalizeThenSuccess(params);
    } else {
        history.push({
            pathname: next_page,
            search: params,
            state: props,
        });
    }

    return dispatch => {
        dispatch({ type: "NAVIGATED" });
    };
}

// Check status on load
export function checkLoadState(page, state, props) {
    switch (page) {
        case "e_verification":
            eVerificationLoadState(state, props);
            break;
        case "document_selection":
            documentSelectionLoadState(state, props);
            break;

        case "ocr_verification":
            ocrVerificationLoadState(state, props);
            break;

        case "group_link_start_page":
        case "group_link_send_page":
            if (!state.groupLink) {
                history.push("/");
            }
            break;

        default:
            return;
    }
}

// Main order of screens
export function firstUncompletedStage(state) {
    if (
        state.screens.e_verification.enabled &&
        (state.screens.e_verification.visible ||
            !IS_OCR_VERIFICATION_ENABLED) &&
        !state.submissionState.e_verification.completed
    ) {
        return "/e_verification";
    } else if (state.screens.mobile_handoff.enabled && !isMobile && !isTablet) {
        return "/mobile_handoff";
    } else if (
        state.screens.document_upload.enabled &&
        !state.submissionState.document_upload.completed
    ) {
        return "/select";
    } else if (
        state.submissionState.document_upload.completed &&
        !state.submissionState.poa_upload.completed &&
        state.screens.poa_upload.enabled
    ) {
        return "/capture/proof_of_address/";
    } else if (isOcrVerificationNeeded(state)) {
        return "/ocr_verification";
    } else if (
        state.screens.passive_liveness.enabled &&
        !state.submissionState.passive_liveness.completed &&
        (isMobile || isTablet) &&
        state.screens.consent.enabled
    ) {
        return "/consent";
    } else if (
        state.screens.passive_liveness.enabled &&
        !state.submissionState.passive_liveness.completed &&
        (isMobile || isTablet)
    ) {
        return "/capture/selfie";
    } else {
        return "/success";
    }
}

export function navigateBack(page, state, props, params) {
    if (page === "document_capture") {
        const next_page = backFromDocumentCapture(state, props);
        history.push({
            pathname: next_page,
            search: params,
            state: props,
        });
    } else {
        goBack();
    }
}

// Go back
export function goBack() {
    history.goBack();
}

export function clearError(context) {
    // Clear any errors
    context.setState({
        navigation: {
            action: "load",
            props: null,
        },
    });
}

// Returns a redirect URL when no mobile handoff was used and a return_url was provided.
function getCompletionRedirectUrl() {
    const state = store.getState();
    const isOnMobileDevice = isMobile || isTablet;
    const submissionState = state.submissionState || {};
    const isHandoff = !!submissionState.isHandoff;
    const isMobileHandoffSubmission =
        !!submissionState.isMobileHandoffSubmission;
    const returnUrl = state.config && state.config.returnUrl;

    if (
        isOnMobileDevice &&
        !isHandoff &&
        !isMobileHandoffSubmission &&
        typeof returnUrl === "string" &&
        returnUrl
    ) {
        return returnUrl;
    }
    return null;
}

// Finalize then Success Page
export function finalizeThenSuccess(params) {
    apiService
        .postFinalizeSubmission()
        .then(response => {
            const redirectUrl = getCompletionRedirectUrl();
            if (redirectUrl) {
                runSubmissionCompletionSideEffects();
                DataDogService.log(
                    "Redirecting to return_url after submission finalize."
                );

                try {
                    window.location.replace(redirectUrl);
                } catch (e) {
                    DataDogService.log(
                        `Redirect to return_url blocked by exception: ${e.message}`
                    );
                    history.push("/success");
                    return;
                }

                // If the browser silently blocked the navigation (CSP,
                // mixed-content, user settings, etc.) we'll still be on
                // the page after a short delay. Fall back to the success
                // page with a manual Continue button.
                setTimeout(() => {
                    DataDogService.log(
                        "Redirect to return_url appears blocked — falling back to success page."
                    );
                    history.push("/success");
                }, 500);
                return;
            }
            history.push("/success");
        })
        .catch(err => {
            DataDogService.createError("Unable to finalize submission.");
            logService.error(err);
            history.push({
                pathname: "/error/default",
                search: params,
                state: { type: "finalize" },
            });
        });
}

// Next Page
export function nextFromAppEntry(state) {
    if (state.groupLink) {
        return "/group_link";
    } else if (state.screens.passive_liveness?.context === "re_verification") {
        return "/re_verification";
    } else if (
        state.screens.start_screen.enabled &&
        state.screens.passive_liveness?.context !== "re_verification"
    ) {
        return "/start";
    } else {
        return firstUncompletedStage(state);
    }
}

export function nextFromGroupLinkStart() {
    return "/group_link_send";
}

// Next Page
export function nextFromStart(state) {
    return firstUncompletedStage(state);
}

// There is no liveness on the desktop flow.
// To prevent liveness from showing go straight to finalise.
// Change this to an error screen?
export function nextFromReverificationStart(state) {
    if (state.screens.mobile_handoff.enabled && !isMobile && !isTablet) {
        return "/mobile_handoff";
    } else if (
        !state.screens.mobile_handoff.enabled &&
        !isMobile &&
        !isTablet
    ) {
        return "/success";
    } else if (state.screens.consent.enabled) {
        return "/consent";
    } else {
        return "/capture/selfie";
    }
}

export function nextFromEVerification(state, props) {
    if (
        state.submissionState.e_verification.result === "pass" &&
        props.eVerificationMode === "document_fallback"
    ) {
        return "/success";
    } else if (state.screens.mobile_handoff.enabled && !isMobile && !isTablet) {
        return "/mobile_handoff";
    } else if (state.screens.document_upload.enabled) {
        return "/select";
    } else {
        return "/e_verification";
    }
}

export function nextFromDocumentSelection(state, props) {
    if (!isMobile && !isTablet && !props.isIdPalCamera) {
        return "/capture/camera";
    } else if (!isMobile && !isTablet && props.isIdPalCamera) {
        return "/capture/idpal_camera/";
    } else {
        return "/instruction/" + props.documentId + "/1";
    }
}

export function nextFromInstructions(props) {
    if (props.isRetry) {
        return nextFromRetry(props);
    } else if (props.isIdPalCamera && !props.isRetry) {
        return "/capture/idpal_camera/";
    } else {
        return "/capture/camera";
    }
}

export function nextFromDocumentCapture(state, props) {
    if (state.submissionState.document_upload.completed) {
        return firstUncompletedStage(state);
    } else if (props.isRetry) {
        return "/capture/photo";
    } else {
        return "/photo/confirm";
    }
}

export function nextFromCapturePhoto(props) {
    if (props.isIdPalCamera) {
        return "/capture/idpal_camera/";
    } else {
        return "/capture/camera";
    }
}

export function nextFromDocumentReview(state, props) {
    if (state.submissionState.document_upload.completed) {
        if (state.screens.poa_upload.enabled) {
            return firstUncompletedStage(state);
        } else if (isOcrVerificationNeeded(state)) {
            return "/ocr_verification";
        } else if (
            state.screens.passive_liveness.enabled &&
            isMobile &&
            state.screens.consent.enabled
        ) {
            return "/consent";
        } else if (state.screens.passive_liveness.enabled && isMobile) {
            return "/capture/selfie";
        } else {
            return "/success";
        }
    }

    if (props.isRetry) {
        if (isMobile || isTablet) {
            return "/instruction/" + props.documentId + "/1";
        } else {
            return "/capture/photo";
        }
    }

    if (props.sidesLeft === 1) {
        if (isMobile || isTablet) {
            return "/instruction/" + props.documentId + "/2";
        } else {
            return "/capture/photo";
        }
    }
}

export function nextFromOcrVerification(state, props) {
    if (isOcrVerificationNeeded(state)) {
        return "/ocr_verification";
    } else if (
        state.screens.passive_liveness.enabled &&
        isMobile &&
        state.screens.consent.enabled
    ) {
        return "/consent";
    } else if (state.screens.passive_liveness.enabled && isMobile) {
        return "/capture/selfie";
    } else {
        return "/success";
    }
}

export function nextFromProofOfAddress(state, props) {
    if (isOcrVerificationNeeded(state)) {
        return "/ocr_verification";
    } else if (
        state.screens.passive_liveness.enabled &&
        isMobile &&
        state.screens.consent.enabled
    ) {
        return "/consent";
    } else if (state.screens.passive_liveness.enabled && isMobile) {
        return "/capture/selfie";
    } else {
        return "/success";
    }
}

export function nextFromConsent() {
    return "/capture/selfie";
}

export function nextFromPassiveLiveness(props) {
    if (props && props.fallback) {
        return "/capture/idpal_selfie";
    } else {
        return "/success";
    }
}

export function backFromDocumentCapture(state, props) {
    if (!isMobile && !isTablet) {
        if (props.sidesLeft === 1) {
            return nextFromRetry(props);
        } else {
            return "/select";
        }
    } else {
        if (props.sidesLeft === 1) {
            return "/instruction/" + props.documentId + "/2";
        } else {
            return "/instruction/" + props.documentId + "/1";
        }
    }
}

function nextFromRetry(props) {
    return props?.isIdPalCamera ? "/capture/idpal_camera/" : "/capture/camera";
}

// Load Checks
export function eVerificationLoadState(state, props) {
    if (state.submissionState.e_verification.completed === 1) {
        navigateNext("e_verification", state, props);
    }
}

export function documentSelectionLoadState(state, props) {
    if (!props.instanceID) {
        history.push("/");
    }
}

export function ocrVerificationLoadState(state, props) {
    return "/ocr_verification";
}
